import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ReportsService } from '../../../services/reports/reports.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { DialogComponent } from '../../core/panels/dialog.component';
import { RadioButtonComponent } from '../../core/controls/radio-button.component';
import { HelpIconComponent } from '../../core/labels/help-icon.component';
import { ReportPreference } from '../../../models/api/reports/report-helpers/report-preference';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-filter-pref-dialog',
  imports: [
    CommonModule,
    ActionRowComponent,
    ButtonComponent,
    ReactiveFormsModule,
    DialogComponent,
    RadioButtonComponent,
    HelpIconComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-dialog #dialog label="Filter preferences" width="40rem">
      <ng-template #content>
        <app-loading-panel
          [state]="loading ? 'Loading' : 'Loaded'"
          loadingStyle="Box"
          [loadingHeight]="200"
        >
          <ng-template #loaded>
            <div *ngTemplateOutlet="body"></div>
          </ng-template>
        </app-loading-panel>
      </ng-template>
    </app-dialog>
    <ng-template #body>
      <form class="" [formGroup]="fg">
        <div class="tw-gap-16 tw-flex tw-pb-4">
          <div class="tw-grow tw-flex tw-gap-2 tw-pt-2">
            <span class="label tw-font-bold">Search by level</span
            ><app-help-icon
              [helpText]="helpText"
              ariaLabel="Search by level information"
              size="20px"
            />
          </div>
          <app-radio-button
            [options]="reports.searchLevelOptions"
            optionValue="value"
            optionLabel="description"
            [control]="fg.controls.searchOption"
            screenReaderLabel="Search by level"
            [vertical]="false"
          />
        </div>
      </form>
      <app-action-row classes="tw-pt-4">
        <app-button
          left1
          label="Restore default"
          emphasis="Low"
          (click)="restoreDefaults()"
        />
        <app-button
          right1
          label="Save"
          [disabled]="loading || !hasChanges"
          (click)="savePref()"
        />
        <app-button right2 label="Cancel" emphasis="Low" (click)="close()" />
      </app-action-row>
    </ng-template>
  `,
  styles: ``,
})
export class FilterPrefDialogComponent extends BaseComponent implements OnInit {
  reports = inject(ReportsService);
  private snackbar = inject(Snackbar);

  @ViewChild('dialog')
  dialog: DialogComponent;

  helpText =
    'Unit level searches and filters are limited to the selected unit(s). Account level searches return a listing of accounts; account filters include all units in the selected account(s).';

  fg = new FormGroup({
    searchOption: new FormControl<string>(null),
  });

  hasChanges = false;
  loading = false;
  initialValues: { searchOption: string } | null = null;
  preferencesChanged = false;

  ngOnInit() {
    this.fg.valueChanges.subscribe(() => {
      this.hasChanges = this.checkForChanges();
    });
  }

  checkForChanges(): boolean {
    if (!this.initialValues) return false;
    const current = this.fg.value;
    return current.searchOption !== this.initialValues.searchOption;
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async show() {
    this.dialog.visible = true;
    await this.loadPreferences(this.preferencesChanged);
    this.preferencesChanged = false;
  }

  @HandleLoadingState()
  async loadPreferences(refreshCache = false) {
    const prefs =
      await this.api.reportHelpers.getReportPreferences(refreshCache);

    // 'N' means no preference saved in DB, default to 'U' (Unit level)
    const accountSearch = prefs?.accountSearch;
    const searchValue =
      !accountSearch || accountSearch === 'N' ? 'U' : accountSearch;
    this.fg.controls.searchOption.setValue(searchValue);

    this.initialValues = { searchOption: this.fg.value.searchOption ?? 'U' };
    this.hasChanges = false;
  }

  close() {
    this.dialog.visible = false;
  }

  restoreDefaults() {
    this.fg.controls.searchOption.setValue('U'); // Unit level is the default
    this.hasChanges = this.checkForChanges();
  }

  @HandleLoadingState()
  async savePref() {
    // Get current preferences first to preserve other fields since its calling the same proc for report prefs
    const currentPrefs =
      await this.api.reportHelpers.getReportPreferences(true);

    const pref: ReportPreference = {
      outputType: currentPrefs?.outputType || 'PDF',
      reportName: currentPrefs?.reportName || 'S',
      autoFitColumns: currentPrefs?.autoFitColumns || 'N',
      accountSearch: this.fg.controls.searchOption.value || 'U',
      includeOnReport: currentPrefs?.includeOnReport || [],
      clientName: currentPrefs?.clientName,
      criteria: currentPrefs?.criteria,
      asOfDate: currentPrefs?.asOfDate,
      scheduleDetails: currentPrefs?.scheduleDetails,
      currencyFormat: currentPrefs?.currencyFormat,
    };

    await this.api.reportHelpers.saveReportPreferences(pref);
    this.snackbar.show('Filter preferences saved successfully', 'Success');

    this.preferencesChanged = true;
    this.initialValues = { searchOption: this.fg.value.searchOption ?? 'U' };
    this.hasChanges = false;
    this.close();
  }
}
